import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'react-hot-toast';
import api, { fileService, templateService } from '../services/api';
import TemplatePreview from '../components/TemplatePreview';

const Import = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [selectedImportType, setSelectedImportType] = useState('marketing');
  const [isUploading, setIsUploading] = useState(false);
  const [importData, setImportData] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [actionResponse, setActionResponse] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [templatePreviews, setTemplatePreviews] = useState({});
  const [loadingPreviews, setLoadingPreviews] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  // Import types configuration
  const importTypes = [
    {
      id: 'appointment_reminder',
      name: t('import.appointmentReminder', 'Appointment Reminder'),
      description: t('import.appointmentReminderDescription', 'Send appointment reminders to patients via WhatsApp'),
      templateFile: '/documents/appointment_reminder_template.xlsx'
    },
    {
      id: 'marketing',
      name: t('import.marketing', 'Marketing'),
      description: t('import.marketingDescription', 'Send marketing messages to patients via WhatsApp'),
      templateFile: '/documents/marketing_template.xlsx'
    }
    // Add more import types here as needed
  ];

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.name.endsWith('.xlsx')) {
      setFile(selectedFile);
      setFileName(selectedFile.name);
      setErrorMessage('');
    } else {
      setFile(null);
      setFileName('');
      const errorMsg = t('import.invalidFileType', 'Please select a valid Excel (.xlsx) file');
      setErrorMessage(errorMsg);
      toast.error(errorMsg);
    }
  };

  const handleDownloadTemplate = () => {
    const selectedType = importTypes.find(type => type.id === selectedImportType);
    if (selectedType && selectedType.templateFile) {
      console.log('Attempting to download template:', selectedType.templateFile);

      // Create a more robust download function
      const link = document.createElement('a');
      link.href = selectedType.templateFile;
      link.download = `${selectedType.id}_template.xlsx`;
      link.target = '_blank'; // Open in new tab as fallback
      link.rel = 'noopener noreferrer';

      // Add error handling
      link.onerror = () => {
        console.error('Failed to download template file');
        const errorMsg = t('import.downloadFailed', 'Failed to download template. Please try again or contact support.');
        toast.error(errorMsg);
      };

      // Add success handling
      link.onload = () => {
        console.log('Template download initiated successfully');
        toast.success(t('import.templateDownloadSuccess', 'Template download started'));
      };

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      console.error('No template file found for selected type:', selectedImportType);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();

    if (!file) {
      const errorMsg = t('import.selectFileFirst', 'Please select a file to upload');
      setErrorMessage(errorMsg);
      toast.error(errorMsg);
      return;
    }

    setIsUploading(true);
    setErrorMessage('');

    try {
      const data = await fileService.importExcelFile(file);
      setImportData(data);
      toast.success(t('import.uploadSuccess', 'File uploaded successfully!'));

      // Load template previews
      await loadTemplatePreviews(data.data);
    } catch (error) {
      console.error('Error uploading file:', error);
      const errorMsg = error.response?.data?.detail || t('import.uploadError', 'Error uploading file');
      setErrorMessage(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsUploading(false);
    }
  };

  const loadTemplatePreviews = async (rows) => {
    if (!rows || rows.length === 0) return;

    setLoadingPreviews(true);
    const previews = {};

    try {
      // Get current user to get clid
      const currentUser = JSON.parse(localStorage.getItem('user'));
      const clid = currentUser?.clid;

      if (!clid) {
        console.error('No CLID found for current user');
        return;
      }

      // Load preview for first row only (for performance)
      const row = rows[0];
      const templateName = getTemplateNameFromRow(row, selectedImportType);
      const contentVariables = getContentVariablesFromRow(row, selectedImportType);

      if (templateName) {
        try {
          const preview = await templateService.getTemplatePreview(clid, templateName, contentVariables);
          previews[0] = preview;
        } catch (error) {
          console.error(`Error loading preview for row 0:`, error);
        }
      }

      setTemplatePreviews(previews);
    } catch (error) {
      console.error('Error loading template previews:', error);
    } finally {
      setLoadingPreviews(false);
    }
  };

  const getTemplateNameFromRow = (row, importType) => {
    if (importType === 'appointment_reminder') {
      return row['Type'] || 'appointment_reminder';
    } else if (importType === 'marketing') {
      return row['Template Name'] || 'marketing_message';
    }
    return null;
  };

  const getContentVariablesFromRow = (row, importType) => {
    if (importType === 'appointment_reminder') {
      return {
        '1': row['Patient Name'] || '',
        '2': row['Date'] || '',
        '3': row['Time'] || '',
        '4': row['Address'] || '',
        '5': row['Remark'] || '',
      };
    } else if (importType === 'marketing') {
      return {
        '1': row['Patient Name'] || '',
        '2': row['Message'] || '',
      };
    }
    return {};
  };

  const handleTakeAction = async () => {
    if (!importData) return;

    setIsProcessing(true);
    setErrorMessage('');

    try {
      const response = await fileService.processImportedData(selectedImportType, importData.data);
      setActionResponse(response);
      toast.success(t('import.processingSuccess', 'Data processed successfully!'));

      // Redirect to job entries page if job_id is available (only for appointment reminders, not marketing)
      if (response.job_id && selectedImportType !== 'marketing') {
        // Show success message briefly before redirecting
        setTimeout(() => {
          navigate(`/appointment-reminder-jobs/${response.job_id}/entries`);
        }, 1500);
      }
    } catch (error) {
      console.error('Error processing import:', error);
      const errorMsg = error.response?.data?.detail || t('import.processingError', 'Error processing import');
      setErrorMessage(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsProcessing(false);
    }
  };

  const resetImport = () => {
    setFile(null);
    setFileName('');
    setImportData(null);
    setActionResponse(null);
    setErrorMessage('');
    setTemplatePreviews({});
    setSelectedRow(null);
    toast.success(t('import.resetSuccess', 'Import form reset successfully'));
  };

  const selectedType = importTypes.find(type => type.id === selectedImportType);

  return (
    <div className="p-4 md:p-6 bg-white rounded-lg shadow">
      <h1 className="text-2xl font-bold mb-6 text-gray-800">{t('import.title', 'Data Import')}</h1>

      {/* Step indicator */}
      <div className="flex mb-8">
        <div className={`flex flex-col items-center w-1/3 ${!importData && !actionResponse ? 'text-blue-600' : 'text-green-600'}`}>
          <div className={`w-10 h-10 flex items-center justify-center rounded-full border-2 ${!importData && !actionResponse ? 'border-blue-600 bg-blue-100' : 'border-green-600 bg-green-100'}`}>
            1
          </div>
          <span className="mt-2 text-sm">{t('import.step1', 'Upload File')}</span>
        </div>
        <div className={`flex flex-col items-center w-1/3 ${importData && !actionResponse ? 'text-blue-600' : importData ? 'text-green-600' : 'text-gray-400'}`}>
          <div className={`w-10 h-10 flex items-center justify-center rounded-full border-2 ${importData && !actionResponse ? 'border-blue-600 bg-blue-100' : importData ? 'border-green-600 bg-green-100' : 'border-gray-300 bg-gray-100'}`}>
            2
          </div>
          <span className="mt-2 text-sm">{t('import.step2', 'Review Data')}</span>
        </div>
        <div className={`flex flex-col items-center w-1/3 ${actionResponse ? 'text-green-600' : 'text-gray-400'}`}>
          <div className={`w-10 h-10 flex items-center justify-center rounded-full border-2 ${actionResponse ? 'border-green-600 bg-green-100' : 'border-gray-300 bg-gray-100'}`}>
            3
          </div>
          <span className="mt-2 text-sm">{t('import.step3', 'Process Data')}</span>
        </div>
      </div>

      {/* Step 1: Upload file */}
      {!importData && (
        <div className="mb-6">
          {/* Import Type Selection */}
          <div className="mb-6">
            <label className="block text-gray-700 text-sm font-medium mb-3">{t('import.importType', 'Import Type')}</label>
            <div className="space-y-3">
              {importTypes.map((type) => (
                <div key={type.id} className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id={`import-type-${type.id}`}
                      name="import-type"
                      type="radio"
                      value={type.id}
                      checked={selectedImportType === type.id}
                      onChange={(e) => setSelectedImportType(e.target.value)}
                      className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor={`import-type-${type.id}`} className="font-medium text-gray-700 cursor-pointer">
                      {type.name}
                    </label>
                    <p className="text-gray-500">{type.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Download Template Button */}
          <div className="mb-6">
            <label className="block text-gray-700 text-sm font-medium mb-2">{t('import.template', 'Template')}</label>
            <button
              type="button"
              onClick={handleDownloadTemplate}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              {t('import.downloadTemplate', 'Download Template Example')}
            </button>
            <p className="mt-2 text-sm text-gray-600">
              {t('import.templateDescription', 'Download the template file to see the required format for')} {selectedType?.name.toLowerCase()}
            </p>
          </div>

          <form onSubmit={handleUpload}>
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-medium mb-2">{t('import.uploadExcelFile', 'Upload Excel File')}</label>
              <div className="mt-1 flex items-center">
                <label className="w-64 flex flex-col items-center px-4 py-6 bg-white text-blue-600 rounded-lg shadow-lg tracking-wide uppercase border border-blue-600 cursor-pointer hover:bg-blue-50">
                  <svg className="w-8 h-8" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                    <path d="M16.88 9.1A4 4 0 0 1 16 17H5a5 5 0 0 1-1-9.9V7a3 3 0 0 1 4.52-2.59A4.98 4.98 0 0 1 17 8c0 .38-.04.74-.12 1.1zM11 11h3l-4-4-4 4h3v3h2v-3z" />
                  </svg>
                  <span className="mt-2 text-base leading-normal">{t('import.selectFile', 'Select a file')}</span>
                  <input type='file' className="hidden" onChange={handleFileChange} accept=".xlsx" />
                </label>
                {fileName && <span className="ml-3 text-gray-700">{fileName}</span>}
              </div>
            </div>
            {errorMessage && <p className="text-red-500 text-sm mb-4">{errorMessage}</p>}
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={isUploading || !file}
                className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${isUploading || !file ? 'bg-blue-300' : 'bg-blue-600 hover:bg-blue-700'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
              >
                {isUploading ? t('import.uploading', 'Uploading...') : t('import.uploadAndPreview', 'Upload and Preview')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Step 2: Display imported data */}
      {importData && !actionResponse && (
        <div className="mb-6">
          <div className="mb-4">
            <h2 className="text-lg font-medium text-gray-800">{t('import.importedDataPreview', 'Imported Data Preview')}</h2>
            <p className="text-sm text-gray-600">
              {t('import.filename', 'Filename')}: {importData.filename} | {t('import.records', 'Records')}: {importData.record_count}
            </p>
          </div>

          {/* Template Preview Section */}
          {loadingPreviews && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-sm text-blue-700">
                {t('import.loadingPreview', 'Loading template preview...')}
              </p>
            </div>
          )}

          {!loadingPreviews && templatePreviews[0] && (
            <div className="mb-6">
              <h3 className="text-md font-medium text-gray-800 mb-3">
                {t('import.messagePreview', 'Message Preview (First Record)')}
              </h3>
              <TemplatePreview preview={templatePreviews[0]} />
            </div>
          )}

          <div className="mb-6 overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {importData.data.length > 0 &&
                    Object.keys(importData.data[0]).map((header, idx) => (
                      <th
                        key={idx}
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        {header}
                      </th>
                    ))
                  }
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {importData.data.map((row, rowIdx) => (
                  <tr key={rowIdx}>
                    {Object.values(row).map((cell, cellIdx) => (
                      <td key={cellIdx} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {typeof cell === 'object' ? JSON.stringify(cell) : String(cell)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex justify-between">
            <button
              onClick={resetImport}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {t('common.back', 'Back')}
            </button>
            <button
              onClick={handleTakeAction}
              disabled={isProcessing}
              className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${isProcessing ? 'bg-blue-300' : 'bg-blue-600 hover:bg-blue-700'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
            >
              {isProcessing ? t('import.processing', 'Processing...') : t('import.processData', 'Process Data')}
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Action Response */}
      {actionResponse && (
        <div className="mb-6">
          <div className="mb-4">
            <h2 className="text-lg font-medium text-gray-800">{t('import.processingComplete', 'Import Processing Complete')}</h2>
          </div>

          <div className="p-4 mb-6 bg-green-50 border border-green-200 rounded-md">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-green-800">{t('common.success', 'Success')}</h3>
                <div className="mt-2 text-sm text-green-700">
                  <p>{actionResponse.message}</p>
                  {actionResponse.job_id && (
                    <div className="mt-2">
                      <p><strong>{t('import.jobId', 'Job ID')}:</strong> {actionResponse.job_id}</p>
                      <p className="text-xs text-green-600 mt-1">
                        {t('import.redirectingMessage', 'Redirecting to job entries page...')}
                      </p>
                    </div>
                  )}
                  {actionResponse.success_count !== undefined && (
                    <div className="mt-2">
                      <p><strong>{t('import.successCount', 'Successfully processed')}:</strong> {actionResponse.success_count}</p>
                      {actionResponse.failed_count > 0 && (
                        <p><strong>{t('import.failedCount', 'Failed to process')}:</strong> {actionResponse.failed_count}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Display failed items if any */}
          {actionResponse.failed && actionResponse.failed.length > 0 && (
            <div className="p-4 mb-6 bg-red-50 border border-red-200 rounded-md">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">{t('import.failedItems', 'Failed Items')}</h3>
                  <div className="mt-2 text-sm text-red-700">
                    <p>{t('import.failedItemsDescription', 'The following items could not be processed:')}</p>
                    <ul className="mt-2 list-disc list-inside space-y-2">
                      {actionResponse.failed.slice(0, 5).map((item, index) => (
                        <li key={index} className="space-y-1">
                          <div className="font-medium">
                            {item.entry?.name || 'Unknown'} ({item.entry?.phone_number || 'Unknown'}) - {item.entry?.appointment_date || 'Unknown'}
                          </div>
                          {item.error && (
                            <div className="text-xs text-red-600 bg-red-100 px-2 py-1 rounded">
                              <strong>{t('import.errorReason', 'Error')}:</strong> {item.error}
                            </div>
                          )}
                        </li>
                      ))}
                      {actionResponse.failed.length > 5 && (
                        <li className="text-gray-600">
                          {t('import.andMore', '... and {{count}} more').replace('{{count}}', String(actionResponse.failed.length - 5))}
                        </li>
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-center">
            <button
              onClick={resetImport}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {t('import.startNewImport', 'Start New Import')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Import;
